import React, { useState, useMemo, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';

import { connect } from 'react-redux';

import properties from '../../../../modules/ajio-util/properties';
import { fetchProducts } from '../../../../actions/shared/productsData';
import productsUtil, { parseProductQueryString, stringifyFacets } from '../../../../utils/products';
import queryUtil from '../../../../modules/ajio-util/query';

import styles from './index.pcss';
import { getLuxeFlags } from '../../../../Flags/selectors';
import { getCurrentStore } from '../../../selectors/metaData';
import { getEnableFilterImprovementPhase2, getVisualFiltersConfig } from '../../../selectors/featureFlagsSelector';
import InlineFiltersShimmer from './InlineFiltersShimmer';
import hexCode from '../../../../modules/ajio-util/hexCode.js';
import {
  buildFiltersArray,
  getDiffFacets,
  fireInlineFilterSeen,
  fireInlineFilterClicked,
  fireFilterApplied,
  fireFilterRemoved,
  computeInlineFilterPosition,
  getRedirectionType
} from '../analytics';

const cx = classNames.bind(styles);

const FIRST_LINE_MAX = 7;
const MAX_VISIBLE_TAGS = 14;

interface FacetValue {
  code: string;
  name: string;
  count: number;
  selected: boolean;
  query: {
    query?: {
      value: string;
    };
    url: string;
  };
}

interface ColorGroup {
  name: string;
  values: FacetValue[];
}

interface L2Group {
  name: string;
  values: FacetValue[];
}

interface L1Group {
  name: string;
  l2Group: L2Group[];
}

interface FacetItem {
  code: string;
  name: string;
  values?: FacetValue[];
  colorGroups?: ColorGroup[];
  l1Group?: L1Group[];
}

interface InlineFilterValue {
  code: string;
  values: string[];
}

interface InlineFilter {
  inlineFilterId: string;
  name: string;
  values: InlineFilterValue[];
}

interface MatchedTag {
  facetCode: string;
  facetValue: FacetValue;
}

type FacetsQuery = Record<string, string[]>;

const getFlatFacetValues = (facet: FacetItem, useGrouped: boolean): FacetValue[] => {
  if (useGrouped) {
    if (facet.colorGroups) {
      const seen = new Set<string>();
      const result: FacetValue[] = [];
      for (const group of facet.colorGroups) {
        for (const val of group.values) {
          if (!seen.has(val.code)) {
            seen.add(val.code);
            result.push(val);
          }
        }
      }
      return result;
    }

    if (facet.l1Group) {
      const seen = new Set<string>();
      const result: FacetValue[] = [];
      for (const l1 of facet.l1Group) {
        for (const l2 of l1.l2Group) {
          for (const val of l2.values) {
            if (!seen.has(val.code)) {
              seen.add(val.code);
              result.push(val);
            }
          }
        }
      }
      return result;
    }
  }

  return facet.values || [];
};

const areFacetsEqual = (a: FacetsQuery, b: FacetsQuery): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key => {
    const valA = [...(a[key] || [])].sort();
    const valB = [...(b[key] || [])].sort();
    if (valA.length !== valB.length) return false;
    return valA.every((v, i) => v === valB[i]);
  });
};

const InlineFilters = props => {
  const {
    isLoadingNextFacets = false,
    isFilterImprovementPhase2Enabled = false,
    filterIndex = 0,
    currentStore = '',
    visualFiltersConfig = {},
    facets,
    luxeFlags = {},
    fetchNextFacets = () => {},
    updateFilterModal = () => {},
    request = {},
    freeTextSearch = ''
  } = props;

  const { query: requestQuery = {} } = request as any;
  const session_id =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sessionId') || '' : '';
  const search_term: string = requestQuery.text || freeTextSearch || '';
  const {
    plpGender = '',
    inlineFiltersFacets = {},
    inlineFilters: rawInlineFilters = []
  } = facets;
  const { allfacetsItems = [] } = inlineFiltersFacets;

  const { inlineFilter = {}, filterIcons = {} } = visualFiltersConfig || {};
  const { title = 'Quick filters for you', autoSkip = false, position: filterFrequency = 6 } = inlineFilter || {};

  const filterPosition = computeInlineFilterPosition(filterIndex, filterFrequency);

  const { isThemeEnable } = luxeFlags;
  const luxe = isThemeEnable === 'ON' && currentStore === 'luxe';
  const fleek = String(currentStore).toLowerCase() === 'ajiogram';

  const inlineFilters = rawInlineFilters.filter(
    (f: InlineFilter) => plpGender === 'general' || f.inlineFilterId !== 'GenderFilters'
  );

  const queryString = queryUtil.parseSearch(location.search) || {};
  const query = parseProductQueryString(queryString.query || '');
  const { facets: appliedFacets = '' } = query;
  const initialFacetsQuery: FacetsQuery = productsUtil.parseFacets(appliedFacets);

  const defaultTabIndex =
    autoSkip && inlineFilters.length > 0 ? filterIndex % inlineFilters.length : 0;

  const [selectedTab, setSelectedTab] = useState<string>(
    inlineFilters.length > 0
      ? inlineFilters[defaultTabIndex]?.inlineFilterId || inlineFilters[0].inlineFilterId
      : ''
  );
  const [localFacetsQuery, setLocalFacetsQuery] = useState<FacetsQuery>(initialFacetsQuery);
  const lastFetchedFacetsRef = useRef<FacetsQuery>(initialFacetsQuery);
  const containerRef = useRef<HTMLDivElement>(null);
  // Refs used inside IntersectionObserver callbacks to access latest state
  const activeTabIdRef = useRef<string>('');
  const inlineFiltersRef = useRef(inlineFilters);
  const tagsCountRef = useRef(0);
  const inlineFilterSeenFiredRef = useRef(false);

  useEffect(() => {
    setLocalFacetsQuery(initialFacetsQuery);
    lastFetchedFacetsRef.current = initialFacetsQuery;
  }, [appliedFacets]);

  // Keep inlineFilters ref in sync for use inside observer callbacks
  useEffect(() => {
    inlineFiltersRef.current = inlineFilters;
  }, [inlineFilters]);

  // Fire "Inline Filter Seen" once per page load when the component first enters the viewport
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || inlineFilterSeenFiredRef.current) return;
          inlineFilterSeenFiredRef.current = true;
          observer.disconnect();
          const currentInlineFilters = inlineFiltersRef.current;
          const activeFilterObj = currentInlineFilters.find(
            (f: InlineFilter) => f.inlineFilterId === activeTabIdRef.current
          );
          fireInlineFilterSeen({
            filterPosition: computeInlineFilterPosition(filterIndex, filterFrequency),
            inlineFilters: currentInlineFilters,
            activeTabName: activeFilterObj?.name || '',
            tagsCount: tagsCountRef.current,
            session_id,
            screen_title: freeTextSearch,
            search_term,
            raw_query: search_term || '',
            redirection_type: getRedirectionType()
          });
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const activeTabId = inlineFilters.find((f: InlineFilter) => f.inlineFilterId === selectedTab)
    ? selectedTab
    : inlineFilters[0]?.inlineFilterId || '';

  // Keep activeTabId ref in sync (must be after activeTabId declaration)
  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  const activeFilter: InlineFilter | undefined = inlineFilters.find(
    (f: InlineFilter) => f.inlineFilterId === activeTabId
  );

  const tags: MatchedTag[] = useMemo(() => {
    if (!activeFilter) return [];

    const matched: MatchedTag[] = [];

    for (const valueGroup of activeFilter.values) {
      const matchingFacet = allfacetsItems.find(
        (facet: FacetItem) => facet.code === valueGroup.code
      );

      if (!matchingFacet) continue;

      const flatValues = getFlatFacetValues(matchingFacet, isFilterImprovementPhase2Enabled);
      if (!flatValues.length) continue;

      for (const valueName of valueGroup.values) {
        const facetValue = flatValues.find((fv: FacetValue) => fv.code === valueName);
        if (facetValue) {
          matched.push({ facetCode: valueGroup.code, facetValue });
        }
      }
    }

    return matched;
  }, [activeFilter, allfacetsItems, isFilterImprovementPhase2Enabled]);

  // Keep tagsCount ref in sync (must be after tags declaration)
  useEffect(() => {
    tagsCountRef.current = tags.length;
  }, [tags.length]);

  const isColorTab = activeFilter?.values.some(
    (v: InlineFilterValue) => v.code === 'verticalcolorfamily'
  );

  const isTagsTab = activeFilter?.values.some((v: InlineFilterValue) => v.code === 'tags');

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [activeTabId]);

  const handleTabClick = (tabId: string) => {
    setSelectedTab(tabId);

    if (areFacetsEqual(localFacetsQuery, lastFetchedFacetsRef.current)) return;

    lastFetchedFacetsRef.current = localFacetsQuery;
    fetchNextFacets({ facets: stringifyFacets(localFacetsQuery) }, 'inlineFiltersFacets');
  };

  const handleTagClick = (facetCode: string, valueCode: string) => {
    const isCurrentlySelected = localFacetsQuery[facetCode]?.includes(valueCode);
    if (!isCurrentlySelected) {
      const clickedFilter = inlineFilters.find((f: InlineFilter) => f.inlineFilterId === activeTabId);
      fireInlineFilterClicked({
        filterPosition,
        inlineFilters,
        clickedTabId: activeTabId,
        clickedTabName: clickedFilter?.name || '',
        tagsCount: tags.length,
        session_id,
        screen_title: freeTextSearch,
        search_term,
        raw_query: search_term || '',
        redirection_type: getRedirectionType()
      });
    }

    setLocalFacetsQuery(prev => {
      const updated = { ...prev };
      const values = updated[facetCode] || [];

      if (values.includes(valueCode)) {
        updated[facetCode] = values.filter(v => v !== valueCode);
        if (updated[facetCode].length === 0) delete updated[facetCode];
      } else {
        updated[facetCode] = [...values, valueCode];
      }

      return updated;
    });
  };

  const isTagSelected = (facetCode: string, valueCode: string): boolean => {
    return localFacetsQuery[facetCode]?.includes(valueCode) || false;
  };

  const hasSelectionChanged = useMemo(() => !areFacetsEqual(localFacetsQuery, initialFacetsQuery), [
    localFacetsQuery,
    appliedFacets
  ]);

  const handleApply = () => {
    const { replaceInHistory = () => {} } = props;

    const addedFacets = getDiffFacets(initialFacetsQuery, localFacetsQuery, true);
    const removedFacets = getDiffFacets(initialFacetsQuery, localFacetsQuery, false);

    const tagPositionMap: Record<string, number> = {};
    tags.forEach((tag, index) => {
      tagPositionMap[`${tag.facetCode}|${tag.facetValue.code}`] = index + 1;
    });

    if (Object.keys(addedFacets).length > 0) {
      fireFilterApplied(
        'Inline',
        buildFiltersArray(addedFacets, allfacetsItems, 1, undefined, tagPositionMap),
        {
          screen_title: freeTextSearch,
          search_term,
          raw_query: search_term || ''
        }
      );
    }
    if (Object.keys(removedFacets).length > 0) {
      fireFilterRemoved(
        'Inline',
        buildFiltersArray(removedFacets, allfacetsItems, 1, undefined, tagPositionMap),
        {
          screen_title: freeTextSearch,
          search_term,
          raw_query: search_term || ''
        }
      );
    }

    window.dispatchEvent(new CustomEvent('filters-applied'));
    replaceInHistory({
      currentPage: 0,
      facets: stringifyFacets(localFacetsQuery)
    });
  };

  const renderTagButton = ({ facetCode, facetValue }: MatchedTag): React.ReactNode => {
    const tagIconUrl = isTagsTab ? filterIcons[facetValue.code.toLowerCase()] : undefined;

    return (
      <button
        key={`${facetCode}-${facetValue.code}`}
        type="button"
        className={cx('tag', {
          'color-tag': isColorTab,
          selected: isTagSelected(facetCode, facetValue.code),
          luxe,
          ajiogram: fleek
        })}
        onClick={() => handleTagClick(facetCode, facetValue.code)}
      >
        {isColorTab && (
          <span
            className={cx('color-circle')}
            style={{ backgroundColor: hexCode(facetValue.code.toLowerCase()) || '#ccc' }}
          />
        )}
        {tagIconUrl && (
          <img
            src={tagIconUrl}
            alt={facetValue.name}
            className={cx('tag-icon')}
            aria-hidden="true"
          />
        )}
        {facetValue.name}
      </button>
    );
  };

  const renderEmptyFilterSection = (): React.ReactNode => {
    return (
      <div className={cx('empty-filter-section')}>
        <p className={cx('empty-filter-title', { ajiogram: fleek })}>No matching options found</p>
        <p className={cx('empty-filter-subtitle', { ajiogram: fleek })}>
          Adjust selected filters to see available options here
        </p>
      </div>
    );
  };

  const renderTags = (): React.ReactNode => {
    if (tags.length <= FIRST_LINE_MAX) {
      return (
        <div className={cx('tags-scroll-container', 'single-line')}>
          <div className={cx('tags-row')}>{tags.map(renderTagButton)}</div>
        </div>
      );
    }

    const row1Tags = tags.slice(0, FIRST_LINE_MAX);
    const row2Tags = isExpanded
      ? tags.slice(FIRST_LINE_MAX)
      : tags.slice(FIRST_LINE_MAX, MAX_VISIBLE_TAGS);
    const showViewMore = !isExpanded && tags.length > MAX_VISIBLE_TAGS;

    return (
      <div className={cx('tags-scroll-container')}>
        <div className={cx('tags-rows-wrapper')}>
          <div className={cx('tags-row')}>{row1Tags.map(renderTagButton)}</div>
          <div className={cx('tags-row')}>
            {row2Tags.map(renderTagButton)}
            {showViewMore && (
              <button
                type="button"
                className={cx('view-more-cta', { luxe, ajiogram: fleek })}
                onClick={() => {
                  updateFilterModal(true);
                  window.location.hash = `#filterBy-${activeFilter?.values?.[0]?.code || ''}`;
                }}
              >
                +View More
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = (): React.ReactNode => {
    if (activeFilter && tags.length === 0) return renderEmptyFilterSection();
    return <div className={cx('tab-content')}>{renderTags()}</div>;
  };

  if (!inlineFilters.length) return null;

  if (isLoadingNextFacets) return <InlineFiltersShimmer luxe={luxe} ajiogram={fleek} />;

  return (
    <div
      ref={containerRef}
      className={cx('inline-filters-container', { luxe, ajiogram: fleek })}
      data-component="inline-filters"
    >
      <div className={cx('header')}>
        <img
          src={`${properties.getProperty('imageBaseUrl')}/static/img/${
            fleek ? 'ajiogram_inline_filter_icon.svg' : 'inline_filter_icon.svg'
          }`}
          alt="Quick Filters"
          className={cx('header-icon')}
        />
        <p className={cx('header-title', { ajiogram: fleek })}>{title}</p>
      </div>

      <div className={cx('tabs-container', { ajiogram: fleek })}>
        {inlineFilters.map((filter: InlineFilter) => (
          <button
            key={filter.inlineFilterId}
            type="button"
            className={cx('tab', {
              ajiogram: fleek,
              selected: activeTabId === filter.inlineFilterId
            })}
            onClick={() => handleTabClick(filter.inlineFilterId)}
          >
            {filter.name}
          </button>
        ))}
      </div>

      {renderTabContent()}

      <button
        type="button"
        className={cx('apply-button', { disabled: !hasSelectionChanged, luxe, ajiogram: fleek })}
        disabled={!hasSelectionChanged}
        onClick={handleApply}
      >
        Apply Filter
      </button>
    </div>
  );
};

function mapStateToProps(state, ownProps) {
  const { facets = {}, request = {}, loaders = {} } = state;

  return {
    facets,
    isLoadingNextFacets: !!loaders?.['fetching-next-facets'],
    request,
    luxeFlags: getLuxeFlags(state?.flags),
    currentStore: getCurrentStore(state),
    visualFiltersConfig: getVisualFiltersConfig(state),
    isFilterImprovementPhase2Enabled: getEnableFilterImprovementPhase2(state),
    freeTextSearch: state?.productsAdditionalInfo?.freeTextSearch
  };
}

const mapDispatchToProps = dispatch => {
  return {
    fetchProducts: params =>
      dispatch(
        fetchProducts({
          request: params.request,
          isRequestOrginIsBrowser: true
        })
      )
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(InlineFilters);
